<template>
  <div class="p-6 bg-white rounded-lg shadow-md">
    <h1 class="text-3xl font-bold mb-6 text-gray-800">Gestion des Départements</h1>

    <div class="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
      <label for="departmentCompany" class="block text-sm font-medium text-gray-700 mb-2">Société</label>
      <select
        id="departmentCompany"
        v-model="selectedCompanyId"
        class="w-full md:w-96 border border-gray-300 rounded-md shadow-sm p-2 bg-white"
      >
        <option v-for="company in companies" :key="company.id" :value="company.id">
          {{ company.name }}
        </option>
      </select>
    </div>

    <div class="mb-4 flex gap-2">
      <input
        type="text"
        v-model="newDepartmentName"
        placeholder="Nouveau département"
        class="flex-1 border border-gray-300 rounded-md shadow-sm p-2"
      />
      <button
        @click="addDepartment"
        class="px-4 py-2 bg-orange-600 text-white font-semibold rounded-md shadow-sm hover:bg-orange-700"
      >
        Ajouter
      </button>
    </div>

    <ul class="border border-gray-200 rounded-md divide-y divide-gray-200 bg-white">
      <li v-for="dept in departments" :key="dept.id" class="px-4 py-3 flex justify-between items-center">
        <span>{{ dept.name }}</span>
        <div class="space-x-3">
          <button @click="renameDepartment(dept)" class="text-blue-600 hover:text-blue-900">Renommer</button>
          <button @click="deleteDepartment(dept.id)" class="text-red-600 hover:text-red-900">Supprimer</button>
        </div>
      </li>
      <li v-if="departments.length === 0" class="px-4 py-3 text-gray-500">Aucun département.</li>
    </ul>

    <p v-if="errorMessage" class="text-red-600 text-sm mt-4">{{ errorMessage }}</p>
    <p v-if="successMessage" class="text-green-600 text-sm mt-4">{{ successMessage }}</p>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import apiFetch, { companyApi } from '@/apiClient';

export default {
  name: 'DepartmentsManagementView',
  setup() {
    const companies = ref([]);
    const selectedCompanyId = ref(null);
    const departments = ref([]);
    const newDepartmentName = ref('');
    const errorMessage = ref('');
    const successMessage = ref('');

    const selectedCompany = () => companies.value.find((company) => company.id === selectedCompanyId.value);

    const fetchCompanies = async () => {
      const data = await apiFetch('/companies');
      companies.value = data;

      const administration = companies.value.find((company) => company.name === 'Administration');
      selectedCompanyId.value = administration ? administration.id : (companies.value[0]?.id || null);
    };

    const fetchDepartments = async () => {
      if (!selectedCompanyId.value) {
        departments.value = [];
        return;
      }
      departments.value = await companyApi.getDepartments(selectedCompanyId.value);
    };

    const addDepartment = async () => {
      const name = newDepartmentName.value.trim();
      if (!selectedCompanyId.value || !name) return;

      await companyApi.createDepartment(selectedCompanyId.value, { name });
      successMessage.value = `Département ajouté pour ${selectedCompany()?.name || 'la société'}.`;
      errorMessage.value = '';
      newDepartmentName.value = '';
      await fetchDepartments();
    };

    const renameDepartment = async (department) => {
      const name = prompt('Nouveau nom du département', department.name);
      if (!name || !name.trim()) return;

      await companyApi.updateDepartment(selectedCompanyId.value, department.id, { name: name.trim() });
      successMessage.value = 'Département mis à jour.';
      errorMessage.value = '';
      await fetchDepartments();
    };

    const deleteDepartment = async (departmentId) => {
      if (!confirm('Supprimer ce département ?')) return;

      await companyApi.deleteDepartment(selectedCompanyId.value, departmentId);
      successMessage.value = 'Département supprimé.';
      errorMessage.value = '';
      await fetchDepartments();
    };

    onMounted(async () => {
      errorMessage.value = '';
      successMessage.value = '';
      try {
        await fetchCompanies();
        await fetchDepartments();
      } catch (error) {
        errorMessage.value = error.message;
      }
    });

    watch(selectedCompanyId, async () => {
      errorMessage.value = '';
      successMessage.value = '';
      try {
        await fetchDepartments();
      } catch (error) {
        errorMessage.value = error.message;
      }
    });

    return {
      companies,
      selectedCompanyId,
      departments,
      newDepartmentName,
      errorMessage,
      successMessage,
      addDepartment,
      renameDepartment,
      deleteDepartment,
    };
  },
};
</script>
